<template>
  <div class="data-table management-main-list-data-table">
    <div class="management-add-button-container">
      <slot />
    </div>

    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title">{{ $t("ListsSettings") }}</span>
        <v-spacer />
        <v-text-field
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          :label="$t('Search')"
          :placeholder="$t('Search')"
          class="data-table-search"
          single-line
          hide-details
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="lists"
        :search="searchFromParent"
        :loading="loading"
        loading-text=""
        :page.sync="pagination.page"
        :items-per-page.sync="paginationSettings.rowsPerPage"
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :header-props="{ sortIcon }"
        :footer-props="{ prevIcon, nextIcon,'items-per-page-options':rowsPerPageItems }"
         @pagination="setRowsOnLocalStorage(dataTableSettingsListId,paginationSettings.rowsPerPage)"
        :class="{ 'no-show-permission': !userHasPermissionTo('show') }"

      >
        <template v-slot:item="props">
          <tr
            :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
            @click="readList(props.item,props.item.custom_list)"
          >
            <td v-if="isSuperAdmin">{{ props.item.code }}</td>

            <td>{{ props.item.name }}</td>

            <td>{{ props.item.client_label }}</td>

            <td>{{ props.item.definition }}</td>

            <td v-if="isSuperAdmin">
              <v-layout v-if="!props.item.hidden" row align-center>
                <div class="company-status-icon mr-3" />
                <span style="text-transform: lowercase">
                  {{ $t("No") }}
                </span>
              </v-layout>

              <v-layout v-else row align-center>
                <div class="company-status-icon status-inactive mr-3" />
                <span style="text-transform: lowercase">
                  {{ $t("Yes") }}
                </span>
              </v-layout>
            </td>

            <td>
              <v-layout v-if="!props.item.custom_list" row align-center>
                <div class="company-status-icon status-inactive mr-3" />
                <span style="text-transform: lowercase">
                  {{ $t("No") }}
                </span>
              </v-layout>

              <v-layout v-else row align-center>
                <div class="company-status-icon mr-3" />
                <span style="text-transform: lowercase">
                  {{ $t("Yes") }}
                </span>
              </v-layout>
            </td>
            <td v-if="props.item.custom_list">
              <app-datatable-action-buttons
                :user-can-edit="canEditList(true)"
                @edit="editItem(props.item,props.item.custom_list)"
                :user-can-delete="canDelete"
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
            <td v-if="!props.item.custom_list">
              <app-datatable-action-buttons
                :user-can-edit="canEditList(false)"
                @edit="editItem(props.item,props.item.custom_list)"
                :user-can-delete="false"
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
          </tr>
        </template>

        <template slot="pageText" slot-scope="item">
          {{ item.pageStart }} - {{ item.pageStop }} {{ $t("tables.Of") }}
          {{ item.itemsLength }}
        </template>

        <template v-slot:no-results>
          <data-table-no-results :search="searchFromParent" />
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />
          <v-alert v-else class="data-table-no-data" icon="warning">
            {{ $t("NoDataLists") }}
          </v-alert>
        </template>
      </v-data-table>
    </div>
    <app-dialog-box
      :open="deleteDialog"
      :title="$t('Delete Custom List')"
      :content="$t('DeleteConfirmation') + ' ' + itemToDelete.name + '?'"
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteField"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import DataTableNoResults from "../../../_Atoms/DataTableNoResults";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
export default {
  name: "DynamicListsDataTable",

  components: {
    AppDatatableActionButtons,
    DataTableNoResults,
    AppDialogBox
  },

  mixins: [managementDatatableMixin],

  data() {
    return {
      permissionName: "list",
      dataTableSettingsListId: "dataTableSettingsListId" ,
      /**
       * Sets the loading of the delete a company dialog button
       */
      loadingDelete: false,
      paginationSettings: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableSettingsListId")
      },
      /**
       * Variable where the lists are stored
       */
      lists: [],
      loading: true,
      loadingDelete: false
    };
  },

  computed: {
    /**
     * Headers for the data table
     * @return {Array}
     */
    headers() {
      let headers = [];
      if (this.isSuperAdmin) {
        headers.push({
          text: this.$i18n.tc("Code", 1),
          align: "left",
          value: "code"
        });
      }
      headers.push(
        { text: this.$i18n.t("List"), align: "left", value: "name" },
        {
          text: this.$i18n.t("ClientLabel"),
          align: "left",
          value: "client_label"
        },
        { text: this.$i18n.t("Definition"), align: "left", value: "definition" }
      );
      if (this.isSuperAdmin) {
        headers.push({
          text: this.$i18n.tc("Hidden", 1),
          align: "left",
          value: "hidden"
        });
      }
      headers.push({
        text: this.$i18n.t("Custom"),
        align: "left",
        value: "custom_list"
      });
      if (this.canEditList) {
        headers.push({
          text: "",
          value: "action",
          sortable: false,
          width: "5%",
          align: "left"
        });
      }
      return headers;
    },
    canDelete(){
      let canDelete;
      if(this.isSuperAdmin){
        canDelete = true;
      }else{
        canDelete = (this.userHasManagementPermission("custom_list", "destroy") && this.isAdmin);
      }
      return canDelete;
    }
  },

  mounted() {
    this.getLists();
  },

  methods: {
    ...mapActions(["GET_LIST_OF_LISTS", "deleteCustomList"]),

    /**
     * Method to get the list of lists of the system
     */
    async getLists() {
      try {
        let result = await this.GET_LIST_OF_LISTS();
        let finalLists = [];
        let listOfLists = result.data.data;
        for (const item of listOfLists) {
          if (this.isSuperAdmin || !item.hidden) {
            finalLists.push(item);
          }
        }
        this.lists = finalLists;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },
    /**
     * Indicates if the user can edit the list
     * @return {Boolean}
     */
    canEditList(custom) {
      let hasPermission;
      if(custom){
        if(this.isSuperAdmin){
          hasPermission = true;
        }else{
          hasPermission = this.userHasManagementPermission("custom_list", "update") && this.isAdmin;
        }
      }else{
        hasPermission = this.isSuperAdmin;
      }
      return hasPermission;
    },
    /**
     * Redirects the user to the details page of the selected list
     * Passes the list name as a parameter of the route
     */
    readList(item, custom) {
      if(custom){
        if(this.userHasManagementPermission("custom_list", "show")){
          let listName = this.getListName(item.code);
          this.$router.push({
            name: "list-setting",
            params: { listName: listName }
          });
        }
      }else{
        let listName = this.getListName(item.code);
        this.$router.push({
          name: "list-setting",
          params: { listName: listName }
        });
      }
    },

    /**
     * Gets the list name from its code
     * @return {String}
     */
    getListName(code) {
      if (code.startsWith("l_")) {
        code = code.replace("l_", "list_");
      }
      return code;
    },

    /**
     * Redirects the user to the editing page of the selected list
     * Passes the list name and a variable indicating if the list is custom or not as parameters of the route
     */
    editItem(item, custom) {
      if(custom){
        let listName = this.getListName(item.code);
        this.$router.push({
          name: "custom-list-setting-edit",
          params: { listName: listName, isCustomList: item.custom_list }
        });
      }else{
        let listName = this.getListName(item.code);
        this.$router.push({
          name: "list-setting-edit",
          params: { listName: listName, isCustomList: item.custom_list }
        });
      }        
    },
    /**
     * Deletes the selected custom field
     * @param item - the item chose by the user
     */
    async deleteField() {
      try {
        this.loadingDelete = true;
        await this.deleteCustomList(this.itemToDelete.uuid);
      } catch (error) {
        console.error(error);
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.deleteDialog = false;
        this.loadingDelete = false;
        await this.getLists(); 
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/SystemSettings/ListsSettings/AllListsDataTable.vue